/**
 * Controls overlay DOM wrapper (issue #87): a semi-translucent, top-right
 * keyboard/mouse legend for the active nav mode, with a small dismissible ⌨
 * toggle whose shown/hidden choice persists in localStorage.
 *
 * This class is deliberately thin: the row data is `controlsBindings.ts`
 * (pure, unit-tested), and the toggle's persistence is `hudPrefs.ts` (pure,
 * unit-tested) — this file only wires the two to real DOM elements. It
 * updates the DOM exclusively from `setMode()` / `setLayoutMap()` /
 * the toggle's click handler, all of which main.ts calls only from
 * mode-change and toggle events, never from the per-frame render loop (hard
 * requirement: zero render-loop work — see main.ts's call sites).
 */
import type { NavMode } from '../camera/cameraRig';
import { CONTROLS_BINDINGS, resolveKeyLabel, type KeycapRow } from './controlsBindings';
import { loadControlsOverlayVisible, saveControlsOverlayVisible, type HudPrefsStorage } from './hudPrefs';

export class ControlsOverlay {
  private readonly panelEl: HTMLElement;
  private readonly toggleEl: HTMLButtonElement;
  private readonly storage: HudPrefsStorage;
  private visible: boolean;
  private mode: NavMode | null = null;
  private layoutMap: ReadonlyMap<string, string> | null = null;

  constructor(panelEl: HTMLElement, toggleEl: HTMLButtonElement, storage: HudPrefsStorage) {
    this.panelEl = panelEl;
    this.toggleEl = toggleEl;
    this.storage = storage;
    this.visible = loadControlsOverlayVisible(storage);
    this.renderVisibility();
    this.toggleEl.addEventListener('click', () => {
      this.visible = !this.visible;
      saveControlsOverlayVisible(this.storage, this.visible);
      this.renderVisibility();
    });
  }

  /**
   * Applies the resolved keyboard layout map (or null on fallback) to the
   * keycaps. Called once the best-effort `navigator.keyboard.getLayoutMap()`
   * promise settles (see `keyboardLayout.ts`) — re-renders the currently
   * shown mode's rows in place so a real (non-QWERTY) layout still updates
   * the legend even though the first paint had to use the QWERTY fallback.
   */
  setLayoutMap(layoutMap: ReadonlyMap<string, string> | null): void {
    this.layoutMap = layoutMap;
    if (this.mode) this.renderRows(this.mode);
  }

  /** Switches the displayed row set to `mode`. No-op if already showing it,
   * so repeat calls from a caller that doesn't itself dedupe are free. */
  setMode(mode: NavMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.renderRows(mode);
  }

  private renderRows(mode: NavMode): void {
    this.panelEl.replaceChildren(...CONTROLS_BINDINGS[mode].map((row) => this.rowEl(row)));
  }

  private rowEl(row: KeycapRow): HTMLElement {
    const rowEl = document.createElement('div');
    rowEl.className = 'controls-row';
    for (const key of row.keys) {
      const keycap = document.createElement('span');
      keycap.className = 'keycap';
      keycap.textContent = resolveKeyLabel(key, this.layoutMap);
      rowEl.appendChild(keycap);
    }
    const label = document.createElement('span');
    label.className = 'controls-label';
    label.textContent = row.label;
    rowEl.appendChild(label);
    return rowEl;
  }

  private renderVisibility(): void {
    this.panelEl.classList.toggle('hidden', !this.visible);
    this.toggleEl.setAttribute('aria-expanded', String(this.visible));
    this.toggleEl.classList.toggle('active', this.visible);
  }
}
